import React, { useState } from 'react';
import { ManuscriptWorkspaceState } from '../src/utils/projectCounts';

interface ProjectIntegrityModalProps {
  isOpen: boolean;
  completionState: ManuscriptWorkspaceState;
  projectId: string;
  onClose: () => void;
}

export function ProjectIntegrityModal({ isOpen, completionState, projectId, onClose }: ProjectIntegrityModalProps) {
  const [showAdmin, setShowAdmin] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-cv-panel border border-cv-border-strong rounded-lg max-w-lg w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b border-cv-border bg-cv-panel/50 flex items-center justify-between">
          <h2 className="text-lg font-black text-cv-primary">Manuscript Integrity</h2>
          <button onClick={onClose} className="text-cv-muted hover:text-cv-primary transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar">
          
          <div className="space-y-3">
             <div className="flex justify-between items-center py-2 border-b border-cv-border">
                <span className="text-sm font-medium text-cv-secondary">Expected Chapters</span>
                <span className="text-sm font-bold text-cv-primary">{completionState.targetChapterCount}</span>
             </div>
             
             <div className="flex justify-between items-center py-2 border-b border-cv-border">
                <span className="text-sm font-medium text-cv-secondary">Saved Chapters</span>
                <span className="text-sm font-bold text-green-400">{completionState.completedChapterCount}</span>
             </div>

             <div className="flex justify-between items-center py-2 border-b border-cv-border">
                <span className="text-sm font-medium text-cv-secondary">Missing Chapters</span>
                <span className={`text-sm font-bold ${completionState.missingChapterNumbers.length > 0 ? 'text-cv-danger' : 'text-cv-primary'}`}>
                   {completionState.missingChapterNumbers.length > 0 ? completionState.missingChapterNumbers.join(', ') : 'None'}
                </span>
             </div>

             <div className="flex justify-between items-center py-2 border-b border-cv-border">
                <span className="text-sm font-medium text-cv-secondary">Duplicate Chapters</span>
                <span className={`text-sm font-bold ${completionState.duplicateChapterNumbers.length > 0 ? 'text-cv-warning' : 'text-cv-primary'}`}>
                   {completionState.duplicateChapterNumbers.length > 0 ? completionState.duplicateChapterNumbers.join(', ') : 'None'}
                </span>
             </div>

             <div className="flex justify-between items-center py-2 border-b border-cv-border">
                <span className="text-sm font-medium text-cv-secondary">Status</span>
                <span className="text-sm font-bold text-cv-primary">
                   {completionState.manuscriptIsComplete ? (
                      <span className="text-green-400">Complete</span>
                   ) : completionState.hasInternalGap ? (
                      <span className="text-cv-warning">Repair required</span>
                   ) : (
                      <span className="text-cv-gold">In Progress</span>
                   )}
                </span>
             </div>

             {completionState.hasInternalGap && completionState.firstMissingChapterNumber && (
               <div className="rounded border border-amber-500/30 bg-cv-warning/10 p-3 text-sm text-cv-warning">
                 Chapter {completionState.firstMissingChapterNumber} is missing while later chapters already exist.
                 {completionState.missingChapterNumbers.length > 1 && (
                   <div className="mt-2 text-xs text-cv-warning/80">
                     All gaps: {completionState.missingChapterNumbers.join(', ')}
                   </div>
                 )}
               </div>
             )}
          </div>

          <div className="pt-4 border-t border-cv-border">
            <button 
              onClick={() => setShowAdmin(!showAdmin)}
              className="text-xs text-cv-muted hover:text-cv-secondary flex items-center gap-1 transition-colors"
            >
              <svg className={`w-3 h-3 transform transition-transform ${showAdmin ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Project Integrity Diagnostics (Admin)
            </button>

            {showAdmin && (
              <div className="mt-3 p-3 bg-cv-page border border-cv-border rounded font-mono text-[10px] text-cv-muted space-y-1">
                 <div>Project ID: <span className="text-cv-interactive">{projectId}</span></div>
                 <div>Invalid Records: <span className={completionState.invalidChapterRecords > 0 ? "text-cv-danger" : "text-green-300"}>{completionState.invalidChapterRecords}</span></div>
                 <div>Orphaned Storage: <span className="text-cv-muted">Not Scanned</span></div>
                 <div>Authoritative Context: <span className="text-cv-interactive">{completionState.isAuthoritative ? "True" : "False"}</span></div>
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-cv-border bg-cv-panel/80 flex justify-end">
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-cv-elevated hover:bg-cv-disabled text-cv-primary rounded text-sm font-bold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
